import { parseRequiredHashtags } from '@/lib/parseRequiredHashtags';
import { getCpmCampaignStatus } from './getCpmCampaignStatus';
import { findCampaignSubmissionDuplicate } from './submissionPlatform';

export const IMPORT_PLATFORMS = ['instagram', 'tiktok', 'youtube'] as const;
export type ImportPlatform = typeof IMPORT_PLATFORMS[number];
export type ImportCampaign = {
  id: string; status: string; is_joined: boolean; campaign_type?: string | null;
  start_date?: string | null; end_date?: string | null; required_hashtags?: string | null; required_mention?: string | null;
  allowed_platforms?: string[] | null;
};
export type ImportConnection = {
  id: string; platform: string; username?: string | null; display_name?: string | null;
  avatar_url?: string | null; status: string; analytics_sync_status?: string | null;
};
export type SocialImportMedia = {
  id: string; platform: string; media_type: string; canonical_url: string;
  caption?: string | null; title?: string | null; thumbnail_url?: string | null;
  published_at?: string | null; availability: string;
  account: { id: string; platform: string; username?: string | null; display_name?: string | null; avatar_url?: string | null };
};
export type ImportSubmission = { platform: string; url: string; disqualified?: boolean };
export type ImportMatch = 'matching' | 'missingTags' | 'unknownCaption' | 'noRules' | 'duplicate' | 'unsupported';
export const isImportPlatform = (platform: string): platform is ImportPlatform => (IMPORT_PLATFORMS as readonly string[]).includes(platform);
export const activeImportConnection = (connection: ImportConnection) => isImportPlatform(connection.platform)
  && connection.status === 'active' && connection.analytics_sync_status !== 'reconnect_required';

function rankingPublicationPeriod(campaign: ImportCampaign) {
  const start = Date.parse(campaign.start_date || '');
  const end = Date.parse(campaign.end_date || '');
  return Number.isFinite(start) && Number.isFinite(end) && start < end ? { start, end } : null;
}

export function canImportCampaign(campaign: ImportCampaign, acceptedTerms: boolean): boolean {
  if (!campaign.is_joined || !acceptedTerms || getCpmCampaignStatus(campaign, new Date()) !== 'active') return false;
  if (campaign.campaign_type !== 'ranking') return true;
  // RankingCampaignPanel also requires the backend-authorized publication phase.
  const period = rankingPublicationPeriod(campaign);
  const now = Date.now();
  return !!period && now >= period.start && now < period.end
    && (!campaign.allowed_platforms?.length || campaign.allowed_platforms.some(isImportPlatform));
}

export function importCaptionTokens(campaign: ImportCampaign, platform: string): string[] {
  if (!isImportPlatform(platform)) return [];
  const hashtags = parseRequiredHashtags(campaign.required_hashtags)[platform];
  return [...new Set([hashtags, campaign.required_mention].filter(Boolean).join(' ').split(/[\s,]+/).filter(Boolean).map(token => token.toLowerCase()))];
}

// Mirrors backend captionTokens.containsCaptionToken. Keep these cases covered
// against platform sections, Unicode boundaries and punctuation in handles.
function containsToken(text: string, token: string) {
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const continuation = token.startsWith('@')
    ? '[\\p{L}\\p{N}_-]|\\.(?=[\\p{L}\\p{N}_.-]*[\\p{L}\\p{N}_-])'
    : '[\\p{L}\\p{N}_]';
  const prefix = /^[#@]/.test(token) ? '' : '(?<![\\p{L}\\p{N}_@#./-])';
  return new RegExp(`${prefix}${escaped}(?!${continuation})`, 'iu').test(text);
}

// Analytics rows store a YouTube description apart from its title (an empty
// description as null); Shorts usually carry their tags in the title. Mirrors
// backend cpmAutoSubmit/importCaption.
function importCaption(media: SocialImportMedia): string | null {
  if (media.platform !== 'youtube') return typeof media.caption === 'string' ? media.caption : null;
  const parts = [media.title, media.caption].filter((part): part is string => typeof part === 'string' && !!part.trim());
  return parts.length ? parts.join('\n') : null;
}

// TikTok's API writes a tagged account as "@<display name>" ("@Rodrigo EL GATO",
// never "@elgato"). With every hashtag literal and one tagged account per
// required mention, the clip may be imported for review. Mirrors backend
// selectAutoSubmitCandidates.tiktokTaggedMentionsMatch.
const TAGGED_ACCOUNT = /(?<![\p{L}\p{N}_])@[\p{L}\p{N}_]/gu;
function tiktokTaggedMentionsMatch(tokens: string[], caption: string) {
  const hashtags = tokens.filter(token => !token.startsWith('@'));
  const mentions = tokens.filter(token => token.startsWith('@'));
  return hashtags.length > 0 && mentions.length > 0 && hashtags.every(token => containsToken(caption, token))
    && (caption.match(TAGGED_ACCOUNT) || []).length >= mentions.length;
}

export function classifyImportMedia(media: SocialImportMedia, campaign: ImportCampaign, submissions: readonly ImportSubmission[]): ImportMatch {
  if (!isImportPlatform(media.platform) || !['video', 'reel', 'short'].includes(media.media_type)
    || media.availability !== 'available' || !media.canonical_url
    || (campaign.allowed_platforms?.length && !campaign.allowed_platforms.includes(media.platform))) return 'unsupported';
  if (campaign.campaign_type === 'ranking') {
    const period = rankingPublicationPeriod(campaign);
    const published = Date.parse(media.published_at || '');
    if (!period || !Number.isFinite(published) || published < period.start
      || published >= period.end || published > Date.now()) return 'unsupported';
  }
  const tokens = importCaptionTokens(campaign, media.platform);
  if (!tokens.length) return 'noRules';
  const caption = importCaption(media);
  if (caption === null) return 'unknownCaption';
  const missing = tokens.filter(token => !containsToken(caption, token));
  if (!missing.length || (media.platform === 'tiktok' && tiktokTaggedMentionsMatch(tokens, caption))) {
    return findCampaignSubmissionDuplicate(media.canonical_url, [...submissions], false) ? 'duplicate' : 'matching';
  }
  if (media.platform === 'tiktok' && (caption.length >= 150 || missing.some(token => token.startsWith('@')))) return 'unknownCaption';
  if (media.platform === 'youtube' && typeof media.caption !== 'string') return 'unknownCaption';
  return 'missingTags';
}
